import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, redirectExternal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Zap, Coins, Settings, Sparkles, Receipt, CheckCircle2 } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
};

export default function Billing() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    api.get("/billing/history").then(({ data }) => setData(data)).catch(() => setData({ transactions: [] }));
  }, []);

  const isPro = data?.plan === "pro";

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      redirectExternal(data.url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg text-white">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-white p-1" data-testid="billing-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-display font-bold text-lg">Fatturazione</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {!data ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            {/* Overview cards */}
            <div className="grid sm:grid-cols-2 gap-5 mb-10">
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="billing-plan-card">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Piano attuale</span>
                  {isPro ? (
                    <span className="flex items-center gap-1.5 text-xs font-mono text-primary border border-primary/40 bg-primary/10 rounded-full px-2.5 py-1"><Zap className="w-3.5 h-3.5" /> PRO</span>
                  ) : (
                    <span className="text-xs font-mono text-muted-foreground border border-white/10 rounded-full px-2.5 py-1">FREE</span>
                  )}
                </div>
                <div className="mt-3 text-2xl font-display font-bold">{isPro ? "Angiò A.I. Pro" : "Piano Free"}</div>
                <p className="text-sm text-muted-foreground mt-1">
                  {isPro
                    ? `Siti illimitati · ${data.ai_limit} generazioni AI / 10 min${data.pro_since ? ` · dal ${fmtDate(data.pro_since)}` : ""}`
                    : `Fino a 3 siti · ${data.ai_limit} generazioni AI / 10 min`}
                </p>
                <div className="mt-5">
                  {isPro ? (
                    <Button onClick={openPortal} disabled={portalLoading} className="bg-white/10 text-white hover:bg-white/15 font-semibold h-10" data-testid="billing-manage-btn">
                      {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Settings className="w-4 h-4 mr-2" /> Gestisci abbonamento</>}
                    </Button>
                  ) : (
                    <Button onClick={() => navigate("/upgrade")} className="bg-primary text-black hover:bg-[#33F3FF] font-semibold h-10" data-testid="billing-upgrade-btn">
                      <Sparkles className="w-4 h-4 mr-2" /> Passa a Pro
                    </Button>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="billing-credits-card">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Crediti AI extra</span>
                  <Coins className="w-4 h-4 text-primary" />
                </div>
                <div className="mt-3 text-4xl font-display font-bold" data-testid="billing-credits-value">{data.ai_credits ?? 0}</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Si usano quando finisci le generazioni del tuo piano. Non scadono.
                </p>
                <div className="mt-5">
                  <Button onClick={() => navigate("/upgrade")} className="bg-white/10 text-white hover:bg-white/15 font-semibold h-10" data-testid="billing-buy-credits-btn">
                    <Coins className="w-4 h-4 mr-2" /> Acquista crediti
                  </Button>
                </div>
              </div>
            </div>

            {/* History */}
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-4 h-4 text-primary" />
              <h2 className="font-display font-semibold text-lg">Storico acquisti</h2>
            </div>
            {data.transactions.length === 0 ? (
              <div className="border border-dashed border-white/15 rounded-2xl py-16 flex flex-col items-center text-center" data-testid="billing-empty">
                <Receipt className="w-9 h-9 text-white/15 mb-4" />
                <p className="text-muted-foreground text-sm max-w-xs">Nessun acquisto ancora. I tuoi pagamenti compariranno qui.</p>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="billing-history-table">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.03] text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium px-4 py-3">Data</th>
                      <th className="text-left font-medium px-4 py-3">Descrizione</th>
                      <th className="text-right font-medium px-4 py-3">Importo</th>
                      <th className="text-right font-medium px-4 py-3">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.transactions.map((t, i) => (
                      <tr key={t.session_id || i} className="border-t border-white/5" data-testid={`billing-row-${i}`}>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(t.created_at)}</td>
                        <td className="px-4 py-3 font-medium">{t.label}</td>
                        <td className="px-4 py-3 text-right font-mono">
                          {t.amount != null ? `€${Number(t.amount).toFixed(2)}` : "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="inline-flex items-center gap-1 text-success text-xs">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Pagato
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
